import React, { useEffect, useState, useCallback } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Input } from "./ui/input";
import { Send, X, Trash2 } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import UserGem from "./UserGem";
import GiftButton from "./GiftButton";

function timeAgo(iso) {
  if (!iso) return "";
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "now";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

export default function PostDetailSheet({ post, open, onOpenChange, onCommentCountChange }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [canComment, setCanComment] = useState(false);

  const load = useCallback(async () => {
    if (!post) return;
    try {
      const { data } = await api.get(`/community/posts/${post.post_id}/comments`);
      setComments(data);
    } catch {
      /* ignore */
    }
  }, [post]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Eligibility check per post (completed order with a tagged vendor / linked product)
  useEffect(() => {
    if (!open || !post || !user) { setCanComment(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/me/can-interact", { params: { post_id: post.post_id } });
        if (!cancelled) setCanComment(!!data.allowed);
      } catch {
        if (!cancelled) setCanComment(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, post?.post_id, user]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/community/posts/${post.post_id}/comments`, { text: text.trim() });
      setComments((c) => [...c, data]);
      setText("");
      onCommentCountChange?.(comments.length + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't comment");
    } finally {
      setSending(false);
    }
  };

  const isStaff = !!user && ["moderator", "admin", "senior_admin", "ceo", "master_admin"].includes(user.role);
  const canDeleteComment = (c) => !!user && (c.author_id === user.user_id || isStaff);

  const deleteComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/community/comments/${c.comment_id}`);
      setComments((arr) => arr.filter((x) => x.comment_id !== c.comment_id));
      onCommentCountChange?.(Math.max(0, comments.length - 1));
      toast.success("Comment moved to Trash");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-zinc-950 border-t border-white/10 text-white h-[85dvh] rounded-t-3xl p-0 flex flex-col"
        data-testid="post-detail-sheet"
      >
        <SheetHeader className="px-5 pt-4 pb-2 border-b border-white/10">
          <SheetTitle className="text-white text-base font-bold">
            {comments.length === 0 ? "Be the first to comment" : `${comments.length} comment${comments.length === 1 ? "" : "s"}`}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          {comments.length === 0 && (
            <p className="text-zinc-500 text-sm text-center py-10" data-testid="comments-empty">No comments yet.</p>
          )}
          {comments.map((c) => (
            <div key={c.comment_id} className="flex items-start gap-2.5 group" data-testid={`comment-${c.comment_id}`}>
              <div className="w-8 h-8 rounded-full bg-pink-500/20 grid place-items-center overflow-hidden border border-pink-400/20 shrink-0">
                {c.author_avatar ? (
                  <img src={fileUrl(c.author_avatar)} alt={c.author_name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-pink-200 text-xs font-bold">{(c.author_name || "?")[0]}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  <span className="font-bold inline-flex items-center gap-1">
                    {c.author_name}
                    <UserGem userId={c.author_id} size="xs" />
                  </span>{" "}
                  <span className="text-zinc-200">{c.text}</span>
                </p>
                <p className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-widest">{timeAgo(c.created_at)}</p>
              </div>
              {canDeleteComment(c) && (
                <button
                  onClick={() => deleteComment(c)}
                  aria-label="Delete comment"
                  className="opacity-0 group-hover:opacity-100 transition-opacity w-7 h-7 grid place-items-center rounded-full text-zinc-500 hover:text-red-400 hover:bg-red-500/10 shrink-0"
                  data-testid={`comment-delete-${c.comment_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              <GiftButton
                recipient={{ user_id: c.author_id, name: c.author_name, picture: c.author_avatar }}
                targetKind="comment"
                targetId={c.comment_id}
                variant="icon"
                testIdPrefix={`comment-gift-${c.comment_id}`}
                className="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 p-3 flex items-center gap-2">
          <Input
            placeholder={
              !user
                ? "Sign in to comment"
                : canComment
                ? "Add a comment…"
                : "Order from a tagged vendor to comment"
            }
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && canComment && send()}
            disabled={!user || sending || !canComment}
            className="h-11 bg-white/5 border-white/10 rounded-full text-white disabled:opacity-60"
            data-testid="comment-input"
          />
          <button
            onClick={send}
            disabled={!user || sending || !text.trim() || !canComment}
            className="h-11 w-11 grid place-items-center rounded-full bg-pink-500 hover:bg-pink-400 disabled:bg-zinc-700 text-white"
            data-testid="comment-send"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
